import { Controller } from "@hotwired/stimulus"

// Records a short video note in the browser (getUserMedia video+audio →
// MediaRecorder → preview → playback) and hands the recorded Blob to the form
// as a File on a hidden <input type=file>, so the normal multipart POST (with
// Rails CSRF) carries it. Robust about the unhappy paths: no camera, denied
// permission, or a browser without MediaRecorder each get a friendly status
// line instead of a crash.
export default class extends Controller {
    static targets = [
        "preview", "playback", "placeholder", "status", "fileInput",
        "startBtn", "recordBtn", "stopBtn", "resetBtn", "submitBtn",
        "deliverWhen", "dateRow"
    ]

    connect() {
        // Prefer WebM (Chrome/Firefox); Safari yields MP4 — both are allowed.
        this._mimeType = this._pickMimeType()
        this._chunks = []
        this._stream = null
        this._recorder = null
    }

    disconnect() {
        this._stopStream()
    }

    async startCamera() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this._setStatus("This browser can't record video. Try a recent Chrome, Firefox or Safari.")
            return
        }
        if (typeof MediaRecorder === "undefined") {
            this._setStatus("This browser doesn't support in-browser recording.")
            return
        }

        this._setStatus("Starting camera…")
        try {
            this._stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        } catch (error) {
            this._setStatus(this._permissionMessage(error))
            return
        }

        if (this.hasPreviewTarget) {
            this.previewTarget.srcObject = this._stream
            this.previewTarget.classList.remove("d-none")
        }
        this._hide(this.placeholderTarget)
        this._hide(this.startBtnTarget)
        this._show(this.recordBtnTarget)
        this._setStatus("Camera ready - hit Record when you are.")
    }

    startRecording() {
        if (!this._stream) return

        this._chunks = []
        let options = this._mimeType ? { mimeType: this._mimeType } : {}
        try {
            this._recorder = new MediaRecorder(this._stream, options)
        } catch (error) {
            // Fall back to the UA default codec if our preferred type failed.
            this._recorder = new MediaRecorder(this._stream)
        }

        this._recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) this._chunks.push(event.data)
        }
        this._recorder.onstop = () => this._finalize()
        this._recorder.start()

        this._hide(this.recordBtnTarget)
        this._show(this.stopBtnTarget)
        this._setStatus("Recording… ")
        this.stopBtnTarget.classList.add("is-recording")
    }

    stopRecording() {
        if (this._recorder && this._recorder.state !== "inactive") {
            this._recorder.stop()
        }
        this._hide(this.stopBtnTarget)
    }

    _finalize() {
        let type = (this._recorder && this._recorder.mimeType) || this._mimeType || "video/webm"
        // Strip any ";codecs=…" so the File type matches our allowed list.
        let baseType = type.split(";")[0]
        let blob = new Blob(this._chunks, { type: baseType })
        let extension = baseType.includes("mp4") ? "mp4" : "webm"
        let file = new File([blob], `video-note.${extension}`, { type: baseType })

        // Assign the recorded File to the hidden input so the form submits it.
        let transfer = new DataTransfer()
        transfer.items.add(file)
        this.fileInputTarget.files = transfer.files

        // Swap the live preview for a playback of what was captured.
        this._stopStream()
        if (this.hasPreviewTarget) {
            this.previewTarget.srcObject = null
            this._hide(this.previewTarget)
        }
        if (this.hasPlaybackTarget) {
            this.playbackTarget.src = URL.createObjectURL(blob)
            this._show(this.playbackTarget)
        }
        this._show(this.resetBtnTarget)
        if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false

        let kb = Math.round(blob.size / 1024)
        this._setStatus(kb > 0 ? `Recorded - ${this._humanSize(blob.size)}. Review it, then send.` : "Recorded - review it, then send.")
    }

    reset() {
        this._chunks = []
        if (this.hasFileInputTarget) this.fileInputTarget.value = ""
        if (this.hasPlaybackTarget) {
            this.playbackTarget.removeAttribute("src")
            this.playbackTarget.load()
            this._hide(this.playbackTarget)
        }
        this._hide(this.resetBtnTarget)
        if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true
        this.startCamera()
    }

    // Show/hide the date field when the timing selector is on "On a date".
    toggleDate() {
        if (!this.hasDeliverWhenTarget || !this.hasDateRowTarget) return
        let onDate = this.deliverWhenTarget.value === "on_date"
        this.dateRowTarget.classList.toggle("d-none", !onDate)
    }

    _pickMimeType() {
        if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return ""
        let candidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
        return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || ""
    }

    _permissionMessage(error) {
        let name = error && error.name
        if (name === "NotAllowedError" || name === "SecurityError") {
            return "Camera and microphone access was blocked. Allow it in your browser settings, then try again."
        }
        if (name === "NotFoundError" || name === "DevicesNotFoundError") {
            return "No camera or microphone found on this device."
        }
        if (name === "NotReadableError") {
            return "Your camera is in use by another app. Close it and try again."
        }
        return "Couldn't start the camera. Check your device and permissions, then try again."
    }

    _humanSize(bytes) {
        if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
        return `${Math.max(1, Math.round(bytes / 1024))} KB`
    }

    _stopStream() {
        if (this._stream) {
            this._stream.getTracks().forEach((track) => track.stop())
            this._stream = null
        }
    }

    _setStatus(text) {
        if (this.hasStatusTarget) this.statusTarget.textContent = text
    }

    _show(el) {
        if (el) el.classList.remove("d-none")
    }

    _hide(el) {
        if (el) el.classList.add("d-none")
    }
}
